import { FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { useAuth } from '../auth/AuthContext';

interface LocationState {
  from?: { pathname?: string };
}

export default function Login() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const redirectTo = state?.from?.pathname || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If already signed in, send them where they were headed.
  useEffect(() => {
    if (status === 'authenticated') {
      navigate(redirectTo, { replace: true });
    }
  }, [status, redirectTo, navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Invalid email or password';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-[340px] rounded-[14px] border border-border bg-panel px-[26px] pb-6 pt-[26px] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)]">
        {/* Brand */}
        <div className="flex flex-col items-center gap-3 mb-5">
          <div className="flex h-[52px] w-[52px] items-center justify-center rounded-[13px] bg-gradient-to-br from-[#5b5bd6] to-[#8b5cf6] text-[22px] font-bold text-white shadow-[0_8px_24px_-8px_rgba(91,91,214,0.55)]">
            P
          </div>
          <div className="text-[14px] font-semibold tracking-tight text-text">Parity</div>
        </div>

        <h1 className="mb-1 text-center text-[16px] font-bold tracking-tight text-text">Sign in</h1>
        <div className="mb-5 text-center text-[12px] text-text-3">Use your dashboard credentials.</div>

        <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-3">Email</span>
            <input
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-[13px] text-text outline-none focus:border-border-strong"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-3">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-[13px] text-text outline-none focus:border-border-strong"
            />
          </label>
          {error && (
            <div className="rounded-md border border-danger/30 bg-danger/10 px-2.5 py-1.5 text-[12px] text-danger">
              {error}
            </div>
          )}
          <Button type="submit" variant="primary" loading={submitting} className="mt-2 w-full justify-center">
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
